import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useForkThread from "features/AskFollowUpQuestionInput/hooks/useForkThread";
import {
  convertDurationToDays,
  getQueryFromFilters,
} from "features/FilterDrawer/utils/queryParams/filterQueryParams";
import useCheckSearchCredits from "features/SearchInput/hooks/useCheckSearchCredits";
import { THREADS_QUERY_KEY } from "features/Sidebar/hooks/useThreads";
import { useCanCreateNewThreadInteraction } from "features/Thread/hooks/useCanCreateNewThreadInteraction";
import { useThreadEvents } from "features/Thread/hooks/useThreadEvents";
import {
  parseThreadInteractionResponse,
  parseThreadResponse,
  Thread,
  ThreadInteraction,
} from "features/Thread/types";
import useCredits from "features/User/hooks/useCredits";
import useUserSettings from "features/User/hooks/useUserSettings";
import {
  addInteractionToThread as addInteractionToThreadAPI,
  deleteThread,
  deleteThreadInteraction,
  getThread,
  Paper,
  ProAgentMode,
  SearchMode,
  ThreadInteractionOverrides,
  updateThread as updateThreadAPI,
} from "helpers/api";
import { FilterParams, threadPath } from "helpers/pageUrl";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import useProToggle from "hooks/useProToggle";
import useSearchMode from "hooks/useSearchMode";
import { useRouter } from "next/router";
import { useCallback } from "react";
import { toast } from "react-toastify";
import { Filters } from "store/slices/search";
import { PremiumFeatureSecondaryButton } from "store/slices/subscription";

/**
 * Generates the query key for thread caching. This is shared between hooks, for instance to allow useNewThread to pre-populate a thread in the query cache.
 */
export function getThreadQueryKey(threadId?: string) {
  return ["thread", threadId];
}

export function getAddThreadInteractionQueryKey(threadId?: string) {
  return ["add-thread-interaction", threadId];
}

// Number of interactions at which to generate a new thread title
const THREAD_TITLE_GENERATION_THRESHOLD = 2;

/**
 * `useThread` is a custom React hook for managing the state and interactions of a thread.
 * It uses `react-query` for fetching, caching, and updating thread data, offering:
 *
 * - **Fetching**: Loads thread data by `threadId`.
 * - **Adding Interactions**: Appends new interactions and updates the cache.
 * - **Updating Interactions**: Modifies specific interactions in the cache.
 */
export function useThread(threadId?: string) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { isProToggleOn } = useProToggle();
  const { isOutOfProSearchCredits, isPremium, spendCreditOnSearch } =
    useCredits();
  const { redirectIfOutOfCredits } = useCheckSearchCredits();
  const { lang } = useUserSettings();
  const { forkThread } = useForkThread(threadId);
  const { changeSearchMode } = useSearchMode();
  const checkCanCreate = useCanCreateNewThreadInteraction();

  const THREAD_QUERY_KEY = getThreadQueryKey(threadId);

  // Fetch thread by ID
  const { data, isLoading, error } = useQuery<Thread, Error>(
    THREAD_QUERY_KEY,
    async () => {
      if (threadId === undefined) throw "ThreadId is undefined";
      const response = await getThread(threadId);
      return parseThreadResponse(response);
    },
    {
      enabled: !!threadId,
    }
  );

  const { trackAddInteraction, trackForkThread, trackSearch } =
    useThreadEvents(data);

  const updateThreadMutation = useMutation(
    async ({ isShared, title }: { isShared?: boolean; title?: string }) => {
      if (threadId === undefined) throw "ThreadId is undefined";
      return await updateThreadAPI(threadId, {
        is_shared: isShared,
        title,
      });
    },
    {
      onMutate: async (variables) => {
        await queryClient.cancelQueries(THREAD_QUERY_KEY);

        const previousData = queryClient.getQueryData<Thread>(THREAD_QUERY_KEY);

        queryClient.setQueryData<Thread>(THREAD_QUERY_KEY, (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            title: variables.title ?? oldData.title,
            is_shared: variables.isShared ?? oldData.is_shared,
          };
        });

        return { previousData };
      },
      onError: (_error, _variables, context: any) => {
        if (context?.previousData) {
          queryClient.setQueryData(THREAD_QUERY_KEY, context.previousData);
        }
      },
      onSuccess: (updatedThread) => {
        // Re-apply values from server in case they differ
        queryClient.setQueryData<Thread>(THREAD_QUERY_KEY, (oldData) => {
          if (!oldData) {
            queryClient.invalidateQueries({ queryKey: THREAD_QUERY_KEY });
            return undefined;
          }

          return {
            ...oldData,
            title: updatedThread.title ?? oldData.title,
            is_shared: updatedThread.is_shared ?? oldData.is_shared,
          };
        });

        // Invalidate the threads list query cache to update the sidebar
        queryClient.invalidateQueries(THREADS_QUERY_KEY);

        // Update the URL if the title changed
        if (updatedThread.title && threadId) {
          const newPath = threadPath(threadId, updatedThread.title);
          router.replace(newPath, undefined, { shallow: true });
        }
      },
    }
  );

  const updateThread = useCallback(
    async ({ title, isShared }: { title?: string; isShared?: boolean }) => {
      if (threadId) {
        await updateThreadMutation.mutateAsync({
          title,
          isShared,
        });
      }
    },
    [threadId, updateThreadMutation]
  );

  const addInteractionMutation = useMutation(
    async ({
      threadId,
      userMessage,
      filters,
      overrides,
      searchMode,
      sources,
    }: {
      threadId: string;
      userMessage: string;
      filters: FilterParams;
      overrides?: ThreadInteractionOverrides;
      searchMode?: SearchMode;
      sources?: Paper[];
    }) => {
      // If the user is out of pro analysis credits, we need to disable pro features.
      // This will mostly not occur due to paywalls, but this will prevent a user that is out of credits
      // from using pro features when clicking follow up questions that do not current have paywalls.
      // TODO(CON-1436): Remove this once we have a paywall in place for suggested follow up questions.
      const isProEnabled = Boolean(
        !!isProToggleOn && (!isOutOfProSearchCredits || isPremium)
      );

      // Only allow sources to be added to pro analysis threads.
      let sourcesToAdd;
      if (searchMode === SearchMode.PRO_ANALYSIS) {
        sourcesToAdd = sources?.map((source) => source.paper_id);
      }

      const response = await addInteractionToThreadAPI({
        threadId,
        userMessage,
        filters,
        overrides,
        isProEnabled,
        lang,
        searchMode,
        sources: sourcesToAdd,
      });

      // Spend credit on the search
      if (response.search_id && response.search_mode) {
        spendCreditOnSearch(response.search_id, response.search_mode);
      }

      // If the user just executed a pro research search, go back to pro analysis mode
      if (searchMode === SearchMode.PRO_RESEARCH) {
        changeSearchMode(SearchMode.PRO_ANALYSIS);
      }

      return parseThreadInteractionResponse(response);
    },
    {
      mutationKey: getAddThreadInteractionQueryKey(threadId),
      onMutate: async ({ userMessage, sources }) => {
        await queryClient.cancelQueries(THREAD_QUERY_KEY);

        // Create a "loading" interaction (client-side only)
        const optimisticInteraction: ThreadInteraction = {
          id: String(Date.now()), // Temporary client-only ID
          user_message: userMessage,
          is_pro_enabled: false,
          created_at: new Date().toISOString(),
          isLoading: true,
          sources: sources,
          mode: ProAgentMode.CONVERSATIONAL, // Default placeholder mode
        };

        // Optimistically update the cache with the loading interaction
        queryClient.setQueryData<Thread>(THREAD_QUERY_KEY, (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            interactions: [...oldData.interactions, optimisticInteraction],
          };
        });

        return { optimisticInteraction };
      },
      // Note: Using "any" to solve type inference issue, can be removed after updating ts:
      // https://tkdodo.eu/blog/react-query-and-type-script#optimistic-updates
      onSuccess: (newInteraction, _, context: any) => {
        queryClient.setQueryData<Thread>(THREAD_QUERY_KEY, (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            interactions: oldData.interactions.map((interaction) =>
              interaction.id === context?.optimisticInteraction.id
                ? newInteraction // Replace loading state with real response
                : interaction
            ),
          };
        });
      },
      onError: (_, __, context) => {
        // Remove the loading interaction if the request fails
        queryClient.setQueryData<Thread>(THREAD_QUERY_KEY, (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            interactions: oldData.interactions.filter(
              (i) => i.id !== context?.optimisticInteraction.id
            ),
          };
        });
      },
      onSettled: () => {
        const oldData = queryClient.getQueryData<Thread>(THREAD_QUERY_KEY);

        // If the thread has enough interactions, refetch thread data to render
        // the new thread title.
        if (
          oldData?.interactions.length === THREAD_TITLE_GENERATION_THRESHOLD
        ) {
          queryClient.invalidateQueries(THREAD_QUERY_KEY);
          // Also invalidate the threads list to update the sidebar
          queryClient.invalidateQueries(THREADS_QUERY_KEY);

          // After refetching, update the URL if the title changed
          queryClient
            .fetchQuery<Thread>(THREAD_QUERY_KEY)
            .then((updatedThread) => {
              if (updatedThread.title && threadId) {
                const newPath = threadPath(threadId, updatedThread.title);
                router.replace(newPath, undefined, { shallow: true });
              }
            });
        }
      },
    }
  );

  /**
   * Add a new interaction to a thread, creating a new ThreadInteraction and saving it to the thread.
   * If the user does not own the thread, a fork of the thread is created and a new interaction is added to the fork.
   * If the user is out of pro analysis credits, and the pro toggle is on, the premium modal is shown and the interaction is not added.
   * If the user is not signed in, the user is redirected to the sign in page.
   *
   * @param userMessage - The user's message to add to the thread.
   * @param overrides - Optional overrides for the thread interaction.
   * @param filters - The filters to use for the thread interaction.
   * @param actionSource - The source of the action that triggered the thread interaction.
   * @param searchMode - The search mode to use for the thread interaction.
   * @param onContinueOrRedirect - A callback function to call when the interaction is allowed and
   * the premium modal does not need to be shown or when the user is not signed in and requires a
   * redirect to the sign in page. Useful for closing modals or other UI elements on a successful interaction.
   * @param isRelatedSearch - Whether the interaction is from a suggested related search.
   */
  const addInteractionToThread = useCallback(
    async ({
      userMessage,
      overrides,
      filters,
      actionSource,
      searchMode,
      onContinueOrRedirect,
      isRelatedSearch,
      sources,
    }: {
      userMessage: string;
      overrides?: ThreadInteractionOverrides;
      filters: Filters;
      actionSource: MixpanelActionSource;
      searchMode: SearchMode;
      onContinueOrRedirect?: () => void;
      isRelatedSearch?: boolean;
      sources?: Paper[];
    }) => {
      if (!threadId || !data) return;

      const [canCreate, message] = checkCanCreate(
        searchMode ?? SearchMode.SUMMARY
      );
      if (!canCreate) {
        toast.error(message);
        return;
      }

      const isOutOfCredits = redirectIfOutOfCredits(
        searchMode,
        actionSource,
        {
          button: PremiumFeatureSecondaryButton.ADD_INTERACTION_TO_THREAD,
          threadId,
          userMessage,
          filters,
        },
        onContinueOrRedirect
      );
      if (isOutOfCredits) return;

      // Call the onContinueOrRedirect callback if it is provided as the interaction is allowed and the premium modal does not need to be shown.
      onContinueOrRedirect?.();

      // Track the search event.
      trackSearch(userMessage, actionSource, isRelatedSearch);

      // If this is a shared thread that we didn't create, fork it and redirect to the copy.
      if (!data.can_interact) {
        // Track the fork event.
        trackForkThread(userMessage, actionSource);

        // Fork the thread.
        await forkThread({
          userMessage,
          filters,
          overrides,
        });
        return;
      }

      // API only accepts "day" units for duration, so convert it before passing.
      const convertedFilters = convertDurationToDays(filters);

      // Track the add interaction event.
      trackAddInteraction(userMessage, actionSource);

      // Add the interaction to the thread.
      await addInteractionMutation.mutateAsync({
        threadId,
        userMessage,
        filters: getQueryFromFilters(convertedFilters),
        overrides,
        searchMode,
        sources: sources,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      threadId,
      addInteractionMutation,
      redirectIfOutOfCredits,
      data,
      forkThread,
      trackSearch,
      trackForkThread,
      trackAddInteraction,
    ]
  );

  // Update an existing query in the thread, such as to save the analysis block.
  const updateThreadInteractionCacheById = useCallback(
    (threadInteractionId: string, updates: Partial<ThreadInteraction>) => {
      queryClient.setQueryData<Thread>(THREAD_QUERY_KEY, (oldData) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          interactions: oldData.interactions.map((interaction) =>
            interaction.id === threadInteractionId
              ? { ...interaction, ...updates }
              : interaction
          ),
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, threadId]
  );

  const deleteInteractionMutation = useMutation(
    async (threadInteractionId: string) => {
      if (threadId === undefined) throw "ThreadId is undefined";
      return await deleteThreadInteraction({
        threadId,
        threadInteractionId,
      });
    },
    {
      onMutate: async (threadInteractionId): Promise<Thread | undefined> => {
        await queryClient.cancelQueries(THREAD_QUERY_KEY);

        const context = queryClient.getQueryData<Thread>(THREAD_QUERY_KEY);

        queryClient.setQueryData<Thread>(THREAD_QUERY_KEY, (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            interactions: oldData.interactions.filter(
              (interaction) => interaction.id !== threadInteractionId
            ),
          };
        });

        return context;
      },
      onError: (_, __, context: Thread | undefined) => {
        if (context) {
          queryClient.setQueryData(THREAD_QUERY_KEY, context);
        }
      },
    }
  );

  // Delete an interaction from the thread.
  const deleteThreadInteractionById = useCallback(
    async (threadInteractionId: string) => {
      if (threadId) {
        await deleteInteractionMutation.mutateAsync(threadInteractionId);
      }
    },
    [threadId, deleteInteractionMutation]
  );

  const deleteThreadMutation = useMutation(
    async (threadId: string) => {
      return await deleteThread({ threadId });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(THREAD_QUERY_KEY);
      },
    }
  );

  // Delete the thread.
  const deleteThreadById = useCallback(
    async (threadId: string) => {
      return deleteThreadMutation.mutateAsync(threadId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [threadId, deleteThreadMutation]
  );

  const isNewInteractionLoading =
    data?.interactions.some((interaction) => interaction.isLoading) ?? false;

  return {
    thread: data,
    error,
    isLoading, // General loading state for fetching the thread
    updateThread,
    isNewInteractionLoading,
    addInteractionToThread,
    deleteThreadInteractionById,
    updateThreadInteractionCacheById,
    deleteThreadById,
    lastThreadInteraction: data?.interactions[data.interactions.length - 1],
  };
}
